import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DateTimeDisplay from "./DateTimeDisplay";
import { useAuth } from "../contexts/AuthContext";
import LoginHistoryTable from "./Settings/LoginHistoryTable";
import {Loading} from "./common/Loading";
import axiosInstance from "../utils/axiosInstance";

const apiUrl =
  window._env_?.REACT_APP_API_URL ||
  "https://app.mylocaleventrentals.com/api";

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);
  const [error, setError] = useState("");
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
   const { logout, getToken } = useAuth();
   const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      const token = getToken();
      if (!token) {
        logout();
        navigate("/login");
        return;
      }
      try {
        const res = await axiosInstance.get(apiUrl + "/user/profile", {
          headers: { "x-auth-token": token },
        });

        setProfile(res.data.user);
        setLoginHistory(res.data.loginHistory || []);
      } catch (err) {
        console.error(err);
        setError("Error fetching profile");
      }finally{
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Change password handler
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage("");

    if (!token) {
      logout();
      navigate("/login");
      return;
    }

    try {
      const res = await axiosInstance.post(
        apiUrl + "/user/change-password",
        { oldPassword, newPassword },
        { headers: { "x-auth-token": token } }
      );

      setPasswordMessage(
        "✅ Password changed successfully. Please login again."
      );
      setOldPassword("");
      setNewPassword("");
      setShowChangePassword(false);

      // clear token to force logout
      localStorage.removeItem("token");
      navigate("/login");
    } catch (err) {
      console.error(err);
      setPasswordMessage(
        err.response?.data?.message || "Error changing password"
      );
    }
  };

  if (error) return <p className="text-red-600">{error}</p>;
  if(isLoading){
    return <Loading/>;
  }

  return (
    <div className="grid p-6 my-6 grid-cols-1 shadow-lg rounded md:rounded-lg">
      <h2 className="text-2xl font-bold mb-4 text-gray-800">My Profile</h2>

      <div className="p-6 min-w-96 mb-6 md:w-90 border border-gray-100 shadow">
        <p>
          <strong>Username:</strong> {profile.username}
        </p>
        <p>
          <strong>Email:</strong> {profile.email}
        </p>
        <p>
          <strong>Role:</strong> {profile.role}
        </p>
        <p>
          <strong>Date Joined:</strong>{" "}
          <DateTimeDisplay value={profile.createdAt} />
        </p>
        {profile.lastLogin && (
          <p>
            <strong>Last Login:</strong>{" "}
            <DateTimeDisplay value={profile.lastLogin} />
          </p>
        )}
        <div className="flex gap-4 mt-3">
          <button className="btn-primary">Edit Profile</button>
          <button
            className="btn-secondary"
            onClick={() => setShowChangePassword(!showChangePassword)}
          >
            Change Password
          </button>
        </div>

        {showChangePassword && (
          <form
            onSubmit={handleChangePassword}
            className="mt-4 flex flex-col gap-3"
          >
            <input
              type="password"
              placeholder="Old Password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              className="border p-2 rounded"
              required
            />
            <input
              type="password"
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="border p-2 rounded"
              required
            />
            <button type="submit" className="btn-primary">
              Update Password
            </button>
            {passwordMessage && (
              <p className="text-sm mt-1">{passwordMessage}</p>
            )}
          </form>
        )}
      </div>

      <h3 className="text-lg font-semibold text-gray-800 mb-3">Login history</h3>
      <LoginHistoryTable rows={loginHistory} />
    </div>
  );
};

export default Profile;